// src/pages/billing/ExpensesPage.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { projectService } from '../../services/project.service';
import { expenseService } from '../../services/expense.service';
import { getCompanySettings } from '../../services/companySettings.service';
import { userService } from '../../services/user.service';
import {
  Expense,
  CreateExpenseDto,
  CompanySettings,
  ExpenseCategory
} from '../../types/finance.types';
import { Project } from '../../types/project.types';
import { User } from '../../types/user.types';
import { format } from 'date-fns';
import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  DollarSign,
  TrendingUp,
  Briefcase,
  Calendar,
  X,
  Upload,
  Receipt,
  Download
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { formatSafeDate } from '../../utils/dateUtils';

export default function ExpensesPage() {
  const { userProfile, currentUser } = useAuth();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);

  // Filters
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [userFilter, setUserFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: ''
  });
  const [searchQuery, setSearchQuery] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    description: '',
    amount: 0,
    categoryId: '',
    categoryName: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    projectId: '',
    projectName: '',
    userId: '',
    userName: '',
    vendor: '',
    paymentMethod: 'card' as 'cash' | 'card' | 'bank_transfer' | 'check' | 'other',
    reimbursementStatus: 'n/a' as 'owed' | 'reimbursed' | 'n/a',
    notes: ''
  });
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);

  const canViewExpenses = ['provider_admin', 'provider_user'].includes(userProfile?.role || '');

  useEffect(() => {
    loadData();
  }, [userProfile]);

  const loadData = async () => {
    if (!userProfile || !canViewExpenses) return;

    setLoading(true);
    try {
      const [expensesData, projectsData, settingsData, usersData] = await Promise.all([
        expenseService.getExpenses(),
        projectService.getUserProjects(userProfile.uid),
        getCompanySettings(),
        userService.getUsers()
      ]);

      setExpenses(expensesData);
      setProjects(projectsData);
      setCompanySettings(settingsData);
      setUsers(usersData);

      // Set default user in form
      if (userProfile) {
        setFormData(prev => ({
          ...prev,
          userId: userProfile.uid,
          userName: userProfile.name || ''
        }));
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (categoryId: string) => {
    const category = companySettings?.expenseCategories.find(c => c.id === categoryId);
    setFormData(prev => ({
      ...prev,
      categoryId,
      categoryName: category?.name || ''
    }));
  };

  const handleProjectChange = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    setFormData(prev => ({
      ...prev,
      projectId: projectId || '',
      projectName: project?.name || ''
    }));
  };

  const handleUserChange = (userId: string) => {
    const user = users.find(u => u.uid === userId);
    setFormData(prev => ({
      ...prev,
      userId,
      userName: user?.name || user?.email || ''
    }));
  };

  const handleCreateExpense = async () => {
    if (!currentUser || !formData.description || !formData.amount || !formData.categoryId) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const expenseData: CreateExpenseDto = {
        description: formData.description,
        amount: formData.amount,
        categoryId: formData.categoryId,
        categoryName: formData.categoryName,
        date: new Date(formData.date + 'T12:00:00'),
        projectId: formData.projectId || undefined,
        projectName: formData.projectName || undefined,
        userId: formData.userId,
        userName: formData.userName,
        vendor: formData.vendor || undefined,
        paymentMethod: formData.paymentMethod,
        reimbursementStatus: formData.reimbursementStatus,
        notes: formData.notes || undefined
      };

      const expense = await expenseService.createExpense(expenseData, currentUser.uid);

      // Upload receipt if provided
      if (receiptFile) {
        setUploadingReceipt(true);
        try {
          await expenseService.uploadReceipt(expense.id, receiptFile);
          toast.success('Expense created with receipt');
        } catch (error) {
          console.error('Error uploading receipt:', error);
          toast.error('Expense created but receipt upload failed');
        } finally {
          setUploadingReceipt(false);
        }
      } else {
        toast.success('Expense created');
      }

      setShowCreateModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error creating expense:', error);
      toast.error('Failed to create expense');
    }
  };

  const handleUpdateExpense = async () => {
    if (!editingExpense) return;

    try {
      await expenseService.updateExpense(editingExpense.id, {
        description: formData.description,
        amount: formData.amount,
        categoryId: formData.categoryId,
        categoryName: formData.categoryName,
        date: new Date(formData.date + 'T12:00:00'),
        projectId: formData.projectId || undefined,
        projectName: formData.projectName || undefined,
        vendor: formData.vendor,
        paymentMethod: formData.paymentMethod,
        reimbursementStatus: formData.reimbursementStatus,
        notes: formData.notes
      });

      setEditingExpense(null);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error updating expense:', error);
    }
  };

  const handleDeleteExpense = async (expenseId: string) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;

    try {
      await expenseService.deleteExpense(expenseId);
      loadData();
    } catch (error) {
      console.error('Error deleting expense:', error);
    }
  };

  const startEditingExpense = (expense: Expense) => {
    setEditingExpense(expense);
    setFormData({
      description: expense.description,
      amount: expense.amount,
      categoryId: expense.categoryId,
      categoryName: expense.categoryName,
      date: formatSafeDate(expense.date, 'yyyy-MM-dd'),
      projectId: expense.projectId || '',
      projectName: expense.projectName || '',
      userId: expense.userId,
      userName: expense.userName,
      vendor: expense.vendor || '',
      paymentMethod: expense.paymentMethod || 'card',
      reimbursementStatus: expense.reimbursementStatus || 'n/a',
      notes: expense.notes || ''
    });
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      description: '',
      amount: 0,
      categoryId: '',
      categoryName: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      projectId: '',
      projectName: '',
      userId: userProfile?.uid || '',
      userName: userProfile?.name || '',
      vendor: '',
      paymentMethod: 'card',
      reimbursementStatus: 'n/a',
      notes: ''
    });
    setReceiptFile(null);
    setEditingExpense(null);
  };

  // CSV export of the filtered expenses (provider-only by route).
  const exportExpensesCSV = () => {
    const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = 'Date,Description,Category,Project,User,Vendor,Payment Method,Reimbursement,Amount,Notes\n';
    const rows = filteredExpenses.map(e => [
      formatSafeDate(e.date, 'yyyy-MM-dd'), e.description, e.categoryName, e.projectName || '',
      e.userName, e.vendor || '', e.paymentMethod || '', e.reimbursementStatus || '',
      e.amount.toFixed(2), e.notes || '',
    ].map(esc).join(',')).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `expenses_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    toast.success('Expenses exported');
  };

  // Filter expenses
  const filteredExpenses = expenses.filter(expense => {
    if (projectFilter !== 'all' && expense.projectId !== projectFilter) return false;
    if (categoryFilter !== 'all' && expense.categoryId !== categoryFilter) return false;
    if (userFilter !== 'all' && expense.userId !== userFilter) return false;
    if (dateRange.start && (expense.date?.toDate?.() || new Date()) < new Date(dateRange.start + 'T12:00:00')) return false;
    if (dateRange.end && (expense.date?.toDate?.() || new Date()) > new Date(dateRange.end + 'T12:00:00')) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        expense.description.toLowerCase().includes(query) ||
        expense.vendor?.toLowerCase().includes(query) ||
        expense.categoryName.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Calculate totals
  const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0);
  const totalOwed = filteredExpenses.filter(e => e.reimbursementStatus === 'owed').reduce((sum, e) => sum + e.amount, 0);

  // Group by category for summary
  const categoryTotals = filteredExpenses.reduce((acc, exp) => {
    if (!acc[exp.categoryId]) {
      acc[exp.categoryId] = { name: exp.categoryName, total: 0, count: 0 };
    }
    acc[exp.categoryId].total += exp.amount;
    acc[exp.categoryId].count += 1;
    return acc;
  }, {} as Record<string, { name: string; total: number; count: number }>);

  // Get active expense categories
  const activeCategories = companySettings?.expenseCategories.filter(c => c.isActive) || [];

  if (!canViewExpenses) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold text-gray-900">Access Denied</h1>
          <p className="text-gray-600 mt-2">You don't have permission to view expenses.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Expenses</h1>
            <p className="mt-1 text-sm text-gray-600">
              Track and manage project expenses
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportExpensesCSV}
              disabled={filteredExpenses.length === 0}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 flex items-center disabled:opacity-50"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </button>
            <button
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Expense
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 bg-red-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-red-600" />
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-1">Total Expenses</p>
            <p className="text-2xl font-bold text-gray-900">${totalExpenses.toFixed(2)}</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 bg-blue-100 rounded-lg">
                <Receipt className="h-5 w-5 text-blue-600" />
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-1">Total Records</p>
            <p className="text-2xl font-bold text-gray-900">{filteredExpenses.length}</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 bg-green-100 rounded-lg">
                <Briefcase className="h-5 w-5 text-green-600" />
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-1">Categories</p>
            <p className="text-2xl font-bold text-gray-900">{Object.keys(categoryTotals).length}</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 bg-purple-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-1">Avg per Expense</p>
            <p className="text-2xl font-bold text-gray-900">
              ${filteredExpenses.length > 0 ? (totalExpenses / filteredExpenses.length).toFixed(2) : '0.00'}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 bg-amber-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-amber-600" />
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-1">Owed (reimbursable)</p>
            <p className="text-2xl font-bold text-gray-900">${totalOwed.toFixed(2)}</p>
          </div>
        </div>

        {/* Category Breakdown */}
        {Object.keys(categoryTotals).length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">Expenses by Category</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {Object.entries(categoryTotals)
                .sort(([, a], [, b]) => b.total - a.total)
                .map(([categoryId, data]) => (
                  <div key={categoryId} className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 truncate">{data.name}</p>
                    <p className="text-lg font-bold">${data.total.toFixed(2)}</p>
                    <p className="text-xs text-gray-400">{data.count} items</p>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search expenses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-md"
                />
              </div>
            </div>

            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 border rounded-md"
            >
              <option value="all">All Categories</option>
              {activeCategories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>

            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              className="px-3 py-2 border rounded-md"
            >
              <option value="all">All Projects</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>

            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="px-3 py-2 border rounded-md"
            >
              <option value="all">All Users</option>
              {users.map(user => (
                <option key={user.uid} value={user.uid}>{user.name || user.email}</option>
              ))}
            </select>

            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="px-3 py-2 border rounded-md"
              placeholder="Start Date"
            />

            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="px-3 py-2 border rounded-md"
              placeholder="End Date"
            />
          </div>
        </div>

        {/* Expenses Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid / Reimb.</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="text-center py-8">
                      <LoadingSpinner className="mx-auto" />
                    </td>
                  </tr>
                ) : filteredExpenses.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-8 text-gray-500">
                      No expenses found
                    </td>
                  </tr>
                ) : (
                  filteredExpenses.map(expense => (
                    <tr key={expense.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {formatSafeDate(expense.date, 'MMM d, yyyy')}
                      </td>
                      <td className="px-6 py-4">
                        <div className="max-w-xs truncate">{expense.description}</div>
                        {expense.notes && (
                          <div className="text-xs text-gray-500 truncate">{expense.notes}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">
                          {expense.categoryName}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {expense.projectName || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {expense.userName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {expense.vendor || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <span className="capitalize">{(expense.paymentMethod || '').replace('_', ' ') || '—'}</span>
                        {expense.reimbursementStatus === 'owed' && <span className="ml-1 text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">owed</span>}
                        {expense.reimbursementStatus === 'reimbursed' && <span className="ml-1 text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">reimb.</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right font-medium">
                        ${expense.amount.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => startEditingExpense(expense)}
                            className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteExpense(expense.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create/Edit Expense Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
              <div className="p-6 border-b">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold">
                    {editingExpense ? 'Edit Expense' : 'Add Expense'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="What was this expense for?"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        value={formData.amount || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                        className="w-full pl-7 pr-3 py-2 border rounded-md"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date *
                    </label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select
                    value={formData.categoryId}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select a category</option>
                    {activeCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project (optional)
                  </label>
                  <select
                    value={formData.projectId}
                    onChange={(e) => handleProjectChange(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">No project</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Incurred By *
                  </label>
                  <select
                    value={formData.userId}
                    onChange={(e) => handleUserChange(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select user</option>
                    {users.map(user => (
                      <option key={user.uid} value={user.uid}>{user.name || user.email}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vendor (optional)
                  </label>
                  <input
                    type="text"
                    value={formData.vendor}
                    onChange={(e) => setFormData(prev => ({ ...prev, vendor: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="Who was paid?"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment method</label>
                    <select
                      value={formData.paymentMethod}
                      onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value as typeof prev.paymentMethod }))}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      {(['card', 'bank_transfer', 'check', 'cash', 'other'] as const).map(m => (
                        <option key={m} value={m}>{m.replace('_', ' ')}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reimbursement</label>
                    <select
                      value={formData.reimbursementStatus}
                      onChange={(e) => setFormData(prev => ({ ...prev, reimbursementStatus: e.target.value as typeof prev.reimbursementStatus }))}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      <option value="n/a">N/A (company-paid)</option>
                      <option value="owed">Owed to staff</option>
                      <option value="reimbursed">Reimbursed</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (optional)
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                    rows={2}
                    placeholder="Additional details..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Receipt (optional)
                  </label>
                  <div className="flex items-center gap-3">
                    <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-indigo-400 hover:bg-indigo-50 transition-colors">
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                        className="hidden"
                      />
                      <Upload className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">
                        {receiptFile ? receiptFile.name : 'Upload receipt'}
                      </span>
                    </label>
                    {receiptFile && (
                      <button
                        type="button"
                        onClick={() => setReceiptFile(null)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  {editingExpense?.receiptUrl && !receiptFile && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                      <Receipt className="h-4 w-4" />
                      <a
                        href={editingExpense.receiptUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:underline"
                      >
                        {editingExpense.receiptFileName || 'View receipt'}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border rounded-md hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={editingExpense ? handleUpdateExpense : handleCreateExpense}
                  disabled={!formData.description || !formData.amount || !formData.categoryId}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  {editingExpense ? 'Update' : 'Add Expense'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
